import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { createUser, getUserById, updateUser, fetchUsers } from '../api/usersApi';

const AdminUserForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditing);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        license: 'Estándar',
        essenNumber: '',
        customLogo: '',
        accentColor: '#6b21a8', // Default purple
        subscriptionExpirationDate: ''
    });

    useEffect(() => {
        if (isEditing) {
            loadUser();
        }
    }, [id]);

    const loadUser = useCallback(async () => {
        try {
            const user = await getUserById(id);
            if (user) {
                setFormData({
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    phoneNumber: user.phoneNumber || '',
                    endPoint: user.endPoint || '',
                    essenNumber: user.essenNumber || '',
                    customLogo: user.customLogo || '',
                    accentColor: user.accentColor || '#6b21a8',
                    license: user.license || 'Estándar',
                    subscriptionExpirationDate: user.subscriptionExpirationDate || ''
                });
            }
        } catch (error) {
            console.error('Error loading user:', error);
            alert('Error al cargar el usuario');
            navigate('/admin/platformUsers');
        } finally {
            setInitialLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        if (isEditing) {
            loadUser();
        }
    }, [isEditing, loadUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Fetch all users to check for duplicates
            const allUsers = await fetchUsers();

            // Check for duplicate phone number (excluding current user when editing)
            const duplicatePhone = allUsers.find(user =>
                user.phoneNumber === formData.phoneNumber &&
                (!isEditing || user.id !== parseInt(id))
            );

            if (duplicatePhone && formData.phoneNumber) {
                alert('El número de teléfono ya está registrado en otro usuario');
                setLoading(false);
                return;
            }

            // Check for duplicate essen number (excluding current user when editing)
            const duplicateEssen = allUsers.find(user =>
                user.essenNumber === Number(formData.essenNumber) &&
                (!isEditing || user.id !== parseInt(id))
            );

            if (duplicateEssen && formData.essenNumber) {
                alert('El número Essen ya está registrado en otro usuario');
                setLoading(false);
                return;
            }

            // Auto-generate endpoint from essenNumber
            const baseURL = process.env.REACT_APP_API_BASE_URL || '';
            const generatedEndpoint = `${baseURL}/${formData.essenNumber}`;

            const payload = {
                ...formData,
                essenNumber: Number(formData.essenNumber) || 0,
                endPoint: generatedEndpoint
            };

            if (isEditing) {
                await updateUser(id, payload);
            } else {
                await createUser(payload);
            }
            navigate('/admin/platformUsers');
        } catch (error) {
            console.error('Error saving user:', error);
            alert('Error al guardar el usuario');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/admin/platformUsers')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Nombre</label>
                        <input
                            type="text"
                            name="firstName"
                            required
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.firstName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Apellido</label>
                        <input
                            type="text"
                            name="lastName"
                            required
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.lastName}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Licencia</label>
                        <select
                            name="license"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.license}
                            onChange={handleChange}
                        >
                            <option value="Estándar">Estándar</option>
                            <option value="Premium">Premium</option>
                            <option value="Mes de prueba">Mes de prueba</option>
                            <option value="Inactivo">Inactivo</option>
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Número Essen</label>
                        <input
                            type="number"
                            name="essenNumber"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.essenNumber}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">URL del Logo Personalizado</label>
                        <input
                            type="text"
                            name="customLogo"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.customLogo}
                            onChange={handleChange}
                            placeholder="https://..."
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Color de Acento</label>
                        <div className="flex gap-4 items-center">
                            <input
                                type="color"
                                name="accentColor"
                                className="h-10 w-20 p-1 rounded border border-gray-300 cursor-pointer"
                                value={formData.accentColor}
                                onChange={handleChange}
                            />
                            <input
                                type="text"
                                name="accentColor"
                                className="w-32 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none uppercase font-mono"
                                value={formData.accentColor}
                                onChange={handleChange}
                                placeholder="#000000"
                                maxLength={7}
                            />
                        </div>
                    </div>

                    {isEditing && formData.subscriptionExpirationDate && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Fecha de Vencimiento de Subscripción</label>
                            <input
                                type="text"
                                disabled
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 outline-none cursor-not-allowed font-medium"
                                value={formData.license === 'Premium' ? 'Sin vencimiento' : new Date(formData.subscriptionExpirationDate).toLocaleDateString()}
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Endpoint (generado automáticamente)</label>
                        <input
                            type="text"
                            name="endPoint"
                            disabled
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-500 outline-none cursor-not-allowed"
                            value={formData.essenNumber ? `${process.env.REACT_APP_API_BASE_URL || ''}/${formData.essenNumber}` : ''}
                            placeholder="Se generará automáticamente"
                        />
                    </div>
                </div>

                <div className="pt-4 flex justify-end gap-4 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/platformUsers')}
                        className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Guardar Usuario
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminUserForm;
